import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGetCompaniesList } from "@/hooks/queries/useCompanies";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import { ChevronDown, Search } from "lucide-react";
import { campaignsService } from "@/services/campaignsService";
import CampaignTable from "@/components/campaign/CampaignTable";
import Dropdown from "@/components/ui/Dropdown";
import Pagination from "@/components/Common/Pagination";
import { useDebounce } from "@/utils/helpers";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import { PER_PAGE } from "@/config";

const ArchivedCampaigns = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const { showToast } = useToastStore;
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: PER_PAGE,
    total: 0,
    last_page: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const { activeTab, setActiveTab } = useGlobalStore();

  const { data } = useGetCompaniesList() || {};
  const companies = data?.companies || [];
  const message = data?.message || "";
  // Debounced search function
  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  // Handle search input change
  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  useEffect(() => {
    if (Array.isArray(companies)) {
      setFilteredCompanies(companies);
    }
  }, [companies]);

  // Fetch campaigns when page, search term, or selected company changes
  useEffect(() => {
    fetchArchivedCampaigns();
  }, [serverPagination.current_page, searchTerm, selectedCompany]);

  const fetchArchivedCampaigns = async () => {
    setIsLoading(true);
    try {
      const { campaigns, pagination } = await campaignsService.getAllCampaigns({
        company_id: selectedCompany,
        page: serverPagination.current_page,
        per_page: serverPagination.per_page,
        search: searchTerm,
        status: [2], // Ensure status is 2 for archived campaigns
      });

      setCampaigns(campaigns);
      setFilteredCampaigns(campaigns);
      setServerPagination((prev) => ({ ...prev, ...pagination }));
    } catch (error) {
      console.error("Error fetching archived campaigns:", error);
      showToast({ message: "Failed to fetch campaigns", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setServerPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  // Add "All Companies" option to the dropdown
  const dropdownOptions = [
    { label: "All Companies", value: null },
    ...filteredCompanies.map((company) => ({
      label: company.name,
      value: company.id,
    })),
  ];

  const handleVacanciesClick = (campaign) => {
    setActiveTab("vacancies");
    navigate(`/companies/view/${campaign.company_id}`, {
      state: {
        campaignId: campaign.id,
      },
    });
  };

  const handleEdit = (id) => {
    navigate(`/campaigns/${id}`); // Navigate to the edit page for the selected campaign
  };

  const handleDelete = (id) => {
    const updatedCampaigns = campaigns.filter((campaign) => campaign.id !== id);
    setFilteredCampaigns(updatedCampaigns);
    localStorage.setItem("campaigns", JSON.stringify(updatedCampaigns)); // Update local storage
    alert("Campaign deleted successfully!");
  };

  // Add this useEffect for click outside handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected?.value || null);
    if (serverPagination.current_page != 1)
      setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  return (
    <div className="w-[90%] mx-auto pt-12 pb-24 flex flex-col justify-start items-start gap-8 px-8">
      {/* Page Header */}
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap content-start">
          <div className="flex-1 min-w-80 inline-flex flex-col justify-start items-start gap-1">
            <h1 className="self-stretch justify-start text-foreground text-2xl font-semibold font-['Inter'] leading-8">
              Archived Campaigns
            </h1>
            <p className="self-stretch justify-start text-muted-foreground text-base font-normal font-['Inter'] leading-6">
              Review and manage your previously active recruitment campaigns.
            </p>
          </div>
        </div>
      </div>

      {/* Controls Section: Search and Dropdown */}
      <div className="self-stretch flex flex-col justify-start items-start gap-6">
        <div className="self-stretch inline-flex justify-between items-start gap-4 flex-wrap">
          {/* Search Box */}
          <div className="w-96 flex flex-col justify-start items-start gap-1.5 font-['Inter']">
            <div className="flex flex-col justify-start items-start gap-1.5">
              <div className="inline-flex justify-start items-center gap-1">
                <span className="text-muted-foreground text-sm font-medium leading-5">
                  Search
                </span>
                <span className="text-foreground text-sm font-medium leading-5">
                  *
                </span>
                <div className="w-4 h-4 text-muted-foreground">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-full h-full"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                  </svg>
                </div>
              </div>
              <div className="self-stretch px-3 py-2 bg-card rounded-lg shadow-sm border border-input focus-within:ring-2 focus-within:ring-secondary/20 focus-within:border-secondary transition-all inline-flex justify-start items-center gap-2 overflow-hidden">
                <div className="flex-1 flex justify-start items-center gap-2">
                  <Search className="w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={tempSearchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-foreground placeholder-muted-foreground text-base font-normal font-['Inter'] leading-6"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="relative z-10">
              <Dropdown
                options={dropdownOptions}
                value={
                  selectedCompany
                    ? {
                        label: filteredCompanies.find(
                          (c) => c.id === selectedCompany,
                        )?.name,
                        value: selectedCompany,
                      }
                    : { label: "All Companies", value: null }
                }
                onChange={handleCompanyChange}
                placeholder="All Companies"
                searchable={true}
                className="w-[240px]"
                dropdownWidth="w-[240px]"
                triggerClassName="!bg-primary-dark !h-8 !px-2.5 !py-1.5 !rounded-lg !border-none shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 !text-white"
                customArrow={<ChevronDown className="w-4 h-4 text-white" />}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full min-h-[400px] items-center justify-center bg-card rounded-xl shadow-sm border border-border">
          <Loader bgColor="transparent" />
        </div>
      ) : (
        <div className="self-stretch flex flex-col gap-6">
          <CampaignTable
            campaigns={filteredCampaigns}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleStatusChange={() => {}}
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            onVacanciesClick={handleVacanciesClick}
            handleSearchChange={handleSearchChange}
            searchTerm={tempSearchTerm}
            noCampaignMessage="No campaigns archived yet"
          />

          <Pagination
            currentPage={serverPagination.current_page}
            totalPages={serverPagination.last_page}
            onPageChange={handlePageChange}
            per_page={serverPagination.per_page}
          />
        </div>
      )}
    </div>
  );
};

export default ArchivedCampaigns;
